import { isSignedAgreement } from "utils/rctiAgreementSigned";
import DrawnSignature from "components/DrawnSignature";
import DocumentPreviewSkeleton from "components/Skeleton/DocumentPreviewSkeleton";
import React, { useCallback, useEffect, useState } from "react";
import SupplierInvitation from "./SupplierInvitation";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { agreementRequest } from "utils/rctiAgreementAPI";
import "./agreements.css";
import "./rctiDesign.css";
import { agreementReference } from "./RctiDesignSystem";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { AgreementOverview } from "./AgreementJourney";
import AgreementDocument from "./AgreementDocument";
import "./supplierAgreement.css";
import "./rctiResponsive.css";
import { savePdfResponse } from "utils/documentPdf";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
export default function PublicRctiAgreement() {
  const { id, mode } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.slice(1));
  const [agreement, setAgreement] = useState(null);
  const [closed, setClosed] = useState(false);
  const [supplierSigner, setSupplierSigner] = useState({
    name: "",
    position: "",
  });
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [gstConsent, setGstConsent] = useState(false);
  const [duplicateConsent, setDuplicateConsent] = useState(false);
  const [electronicConsent, setElectronicConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const load = useCallback(async () => {
    const saved = await agreementRequest(
      `/public/${mode}/${id}/view`,
      { token },
      "post",
      true
    );
    setAgreement(saved);
    setSupplierSigner(
      saved.supplierSigner ||
        saved.supplierRepresentative || { name: "", position: "" }
    );
  }, [id, mode, token]);
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);
  const canAccept = Boolean(
    signature &&
      consent &&
      gstConsent &&
      duplicateConsent &&
      electronicConsent &&
      supplierSigner.name.trim() &&
      supplierSigner.position.trim()
  );
  const closeInvitation = () => {
    setSignature("");
    setConsent(false);
    setGstConsent(false);
    setDuplicateConsent(false);
    setElectronicConsent(false);
    setClosed(true);
  };
  const act = async (action) => {
    if (busy || (action === "accept" && !canAccept)) return;
    setBusy(true);
    setError("");
    try {
      const result = await agreementRequest(
        `/public/${mode}/${id}/${action === "suspend" ? "suspend" : "respond"}`,
        {
          token,
          action,
          signature,
          consent: consent && duplicateConsent && electronicConsent,
          gstConsent,
          supplierSigner,
        },
        "post",
        true
      );
      setMessage(
        result.notificationSent === false
          ? "Agreement saved. Buyer notification delivery failed; please contact the buyer."
          : action === "accept" && result.supplierEmailed === false
          ? "Agreement signed, but your suspension-link email could not be delivered. Ask the buyer to resend the supplier link from RCTI Agreements."
          : action === "accept" && result.emailed === false
          ? "Agreement signed, but the buyer’s suspension-link email could not be delivered. The buyer can resend it from RCTI Agreements."
          : action === "accept"
          ? "Agreement signed. The buyer has been notified."
          : action === "reject"
          ? "Agreement rejected. The buyer has been notified."
          : "Agreement suspended."
      );
      if (result.notificationSent === true &&
        (action !== "accept" || (result.emailed === true && result.supplierEmailed === true))) {
        showDraftSavedMessage("Email sent successfully");
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const resendSuspensionLink = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await agreementRequest(
        `/public/review/${id}/supplier-email`,
        { token },
        "post",
        true
      );
      if (!result.sent)
        throw new Error(
          "The suspension-link email could not be delivered. Please retry later or contact the buyer."
        );
      setMessage("Your suspension-link email has been sent.");
      showDraftSavedMessage("Email sent successfully");
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };
  const downloadPdf = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await agreementRequest(
        `/public/${mode}/${id}/pdf`,
        { token },
        "post",
        true
      );
      savePdfResponse({ data: result.pdf }, result.filename);
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };
  const showInvitation =
    agreement &&
    mode === "review" &&
    ["proposed", "pending"].includes(agreement.status) &&
    !["sign", "agreement"].includes(search.get("step"));
  const showSigning =
    agreement &&
    mode === "review" &&
    ["proposed", "pending"].includes(agreement.status) &&
    search.get("step") === "sign";
  const openSigning = () =>
    navigate({ search: "?step=sign", hash: window.location.hash });
  if (closed)
    return (
      <main className="rcti-agreements rcti-designed rcti-invitation-closed">
        <h1>Agreement session closed</h1>
        <p>
          No further signing action was taken. You can close this tab or reopen
          the agreement below.
        </p>
        <button type="button" onClick={() => setClosed(false)}>
          Reopen agreement
        </button>
      </main>
    );
  return (
    <main className="rcti-agreements rcti-agreements--public rcti-designed rcti-supplier-page">
      <header className="rcti-public-topbar">
        <strong>invoice &amp; recover</strong>
        <span>
          Secure agreement invitation
          {agreement ? ` · ${agreement.supplier.name}` : ""}
        </span>
      </header>
      <div className="rcti-agreements__content">
        <button
          type="button"
          className="rcti-design-back"
          disabled={busy}
          onClick={closeInvitation}
        >
          {(showInvitation || showSigning)
            ? "Close invitation"
            : "Close agreement"}
        </button>
        {showInvitation ? (
          <SupplierInvitation
            agreement={agreement}
            onReview={() =>
              navigate({
                search: "?step=agreement",
                hash: window.location.hash,
              })
            }
          />
        ) : (
          <>
            <header className="rcti-design-heading">
              <div>
                <h1>
                  {showSigning
                    ? "Review & sign agreement"
                    : agreement
                    ? `Agreement ${agreementReference(agreement._id)}`
                    : "RCTI agreement"}
                </h1>
                <p>
                  {agreement
                    ? `${agreement.buyer.name} invites you to ${
                        ["pending", "proposed"].includes(agreement.status)
                          ? "accept"
                          : "view"
                      } ${agreementReference(agreement._id)} for ${
                        agreement.supplier.name
                      }.`
                    : "Review your secure agreement invitation."}
                </p>
              </div>
            </header>
            {error && (
              <p role="alert" className="rcti-agreements__error">
                {error}
              </p>
            )}
            {message && (
              <div role="status" className="rcti-design-notice">
                <strong>{message}</strong>
                {agreement?.status === "active" && (
                  <p>
                    You accepted wording v{agreement.version} at{" "}
                    {rctiTimestamp(agreement.supplierSignedAt)}. No tax invoice
                    or payable was created.
                  </p>
                )}
              </div>
            )}
            {!agreement && !error && (
              <DocumentPreviewSkeleton label="Loading RCTI agreement…" />
            )}
            {agreement && !showSigning && (
              <AgreementOverview
                agreement={agreement}
                audience="supplier"
                onClose={closeInvitation}
                primaryAction={
                  mode === "review" &&
                  ["pending", "proposed"].includes(agreement.status) ? (
                    <button onClick={openSigning}>
                      Review &amp; sign agreement
                    </button>
                  ) : isSignedAgreement(agreement) ? (
                    <button disabled={busy} onClick={downloadPdf}>
                      Download accepted agreement
                    </button>
                  ) : null
                }
                actions={
                  <div className="rcti-agreements__actions">
                    {isSignedAgreement(agreement) && (
                      <>
                        <button disabled={busy} onClick={downloadPdf}>
                          Download agreement PDF
                        </button>
                        <button onClick={() => window.print()}>
                          Print / save agreement
                        </button>
                      </>
                    )}
                    {mode === "review" && agreement.status === "active" && (
                      <button disabled={busy} onClick={resendSuspensionLink}>
                        Email my suspension link
                      </button>
                    )}
                    {agreement.status === "active" &&
                      ["manage", "supplier"].includes(mode) && (
                        <button
                          className="rcti-button-danger"
                          disabled={busy}
                          onClick={() => {
                            if (
                              window.confirm("Suspend this RCTI agreement now?")
                            )
                              act("suspend");
                          }}
                        >
                          Suspend agreement
                        </button>
                      )}
                  </div>
                }
              >
                <section className="rcti-agreement-card">
                  <h2>Linked RCTIs</h2>
                  <p>
                    Issued RCTIs are sent separately by {agreement.buyer.name}{" "}
                    once successfully issued.
                  </p>
                  <p>
                    {["pending", "proposed"].includes(agreement.status)
                      ? "No RCTIs can be issued under this agreement before acceptance. "
                      : ""}
                    Unissued buyer drafts are not supplier tax documents.
                  </p>
                </section>
              </AgreementOverview>
            )}
            {agreement && showSigning && (
              <section className="rcti-public-workspace agreement-public-workspace">
                <AgreementDocument agreement={agreement} variant="supplier" />
                {mode === "review" &&
                  ["proposed", "pending"].includes(agreement.status) && (
                    <aside className="supplier-signing-sidebar">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          act("accept");
                        }}
                      >
                        <div className="rcti-agreement-signing-intro">
                          <h2>Sign for {agreement.supplier.name}</h2>
                          <p>
                            Review wording v{agreement.version}, confirm your
                            authority and draw your signature to accept for the
                            supplier.
                          </p>
                        </div>
                        <label>
                          Full legal name
                          <input
                            required
                            maxLength={160}
                            value={supplierSigner.name}
                            onChange={(event) =>
                              setSupplierSigner({
                                ...supplierSigner,
                                name: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          Position / authority
                          <input
                            required
                            maxLength={160}
                            value={supplierSigner.position}
                            onChange={(event) =>
                              setSupplierSigner({
                                ...supplierSigner,
                                position: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          Email
                          <input
                            readOnly
                            value={
                              agreement.supplier.emailAddress || "Not recorded"
                            }
                          />
                        </label>
                        <label className="rcti-consent">
                          <input
                            type="checkbox"
                            required
                            checked={gstConsent}
                            onChange={(e) => setGstConsent(e.target.checked)}
                          />
                          I confirm the supplier is registered for GST and will
                          notify the buyer if this changes.
                        </label>
                        <label className="rcti-consent">
                          <input
                            type="checkbox"
                            required
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                          />
                          I have read and accept all terms and am authorised to
                          sign for and bind the supplier.
                        </label>
                        <label className="rcti-consent">
                          <input
                            type="checkbox"
                            required
                            checked={duplicateConsent}
                            onChange={(e) =>
                              setDuplicateConsent(e.target.checked)
                            }
                          />
                          I agree the supplier will not issue duplicate tax
                          invoices for the covered supplies.
                        </label>
                        <label className="rcti-consent">
                          <input
                            type="checkbox"
                            required
                            checked={electronicConsent}
                            onChange={(e) =>
                              setElectronicConsent(e.target.checked)
                            }
                          />
                          I consent to electronic signing and adopt the drawing
                          below as my signature.
                        </label>
                        <DrawnSignature
                          value={signature}
                          onChange={setSignature}
                          disabled={busy}
                        />
                        <p className="supplier-signing-date">
                          Signing date and time · Recorded on acceptance
                        </p>
                        <button disabled={busy || !canAccept}>
                          Sign &amp; accept agreement
                        </button>
                        <p>
                          This agreement becomes effective only on acceptance.
                          No tax invoice or payable is created.
                        </p>
                      </form>
                      <section className="supplier-before-accept">
                        <h2>Before you accept</h2>
                        <p>
                          The buyer determines the value and issues RCTIs for
                          covered supplies. The supplier must not issue
                          duplicate tax invoices. Both parties can suspend this
                          agreement later.
                        </p>
                        <button
                          type="button"
                          className="rcti-button-secondary"
                          disabled={busy}
                          onClick={closeInvitation}
                        >
                          Close without signing
                        </button>
                        <button
                          type="button"
                          className="rcti-button-secondary"
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm("Reject this RCTI agreement?"))
                              act("reject");
                          }}
                        >
                          Reject agreement
                        </button>
                      </section>
                    </aside>
                  )}
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
